import React, { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Card } from "@tennr/frontend/components/Card";
import "./CaseStudiesCarousel.css";
import useEmblaCarousel from "embla-carousel-react";
import { type EmblaCarouselType } from "embla-carousel";
import type { CaseStudyV2 as SanityCaseStudyV2 } from "@tennr/frontend/sanity.types";
import { urlForImage } from "@tennr/frontend/lib/sanity";

interface CaseStudiesCarouselProps {
  caseStudies?: SanityCaseStudyV2[];
}

export const CaseStudiesCarousel: React.FC<CaseStudiesCarouselProps> = ({ caseStudies }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ inViewThreshold: 1, loop: true });
  const [slidesInView, setSlidesInView] = useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const featuredCaseStudies = caseStudies?.filter(
    ({ slug }) =>
      slug?.current &&
      ["independent-medical-equipment", "neb-medical-services", "eastern-medtech"].includes(slug.current)
  );

  const scrollTo = (index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  };

  useEffect(() => {
    if (!emblaApi) return;

    const updateSlidesInView = (emblaApi: EmblaCarouselType): void => {
      setSlidesInView(emblaApi.slidesInView());
      setSelectedIndex(emblaApi.selectedScrollSnap()); // 👈 Add this
    };

    updateSlidesInView(emblaApi);
    emblaApi.on("slidesInView", updateSlidesInView);
    emblaApi.on("reInit", updateSlidesInView);
    emblaApi.on("select", updateSlidesInView); // 👈 Also listen for slide selection
  }, [emblaApi]);

  return (
    <div className="case-studies-carousel" ref={emblaRef}>
      <div className="case-studies-carousel-slides">
        {featuredCaseStudies?.map(({ heroSection, companyLogo, slug }, i) => {
          const heading = heroSection?.heading;
          return (
            <div
              key={i}
              className={`case-studies-carousel-slide-container ${slidesInView.includes(i) ? "" : "inactive"}`}
            >
              <Link
                to={`/customer-stories/${slug?.current}`}
                disabled={!slidesInView.includes(i)}
                onClick={() => scrollTo(i)}
              >
                <Card className="case-studies-carousel-slide">
                  <img
                    src={urlForImage(companyLogo)}
                    alt=""
                    className="case-studies-carousel-slide-image"
                    draggable={false}
                  />
                  <div className="case-studies-carousel-slide-copy">
                    <h3 className="case-studies-carousel-slide-title">{heading}</h3>
                    <div className="case-studies-carousel-slide-cta">
                      <span>Read case study</span>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          );
        })}
      </div>
      {featuredCaseStudies && (
        <div className="carousel-dots">
          {featuredCaseStudies.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${selectedIndex === index ? "active" : ""}`}
              onClick={() => scrollTo(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
